import { matchPath } from 'react-router-dom'
import type { AuthSession } from '@/features/auth/types'
import { ClientTypes, UserTypes } from '@/types/authorization'
import type { PermissionKey } from '@/types/authorization'
import { hasRouteAccess, hasRouteVisibility, renderProtectedRoute, resolveRoutePath } from '@/routes/helpers'
import type { ProtectedRouteDescriptor, ResolvedNavSection } from '@/routes/types'
import { complianceRouteDescriptors } from '@/features/compliance/routing'
import { advisoryRouteDescriptors } from '@/features/advisory-ai/routing'
import { practiceAgentRouteDescriptors } from '@/features/practice-agent/routing'
import { capacityPlannerRouteDescriptors } from '@/features/capacity-planner/routing'
import { getNavSectionConfig } from '@/config/navSections'
import { assertAdvicePrepPaywalledRoutesExist } from '@/features/adviceprep/paywall/constants'
import { administrationRoutes } from './administrationRoutes'
import { complianceRoutes } from './complianceRoutes'
import { integrationsRoutes } from './integrationsRoutes'
import { manageRoutes } from './manageRoutes'
import { practiceRoutes } from './practiceRoutes'
import { profileRoutes } from './profileRoutes'

export const protectedRouteDescriptors: ProtectedRouteDescriptor[] = [
  ...manageRoutes,
  ...practiceRoutes,
  ...practiceAgentRouteDescriptors,
  ...capacityPlannerRouteDescriptors,
  ...advisoryRouteDescriptors,
  ...complianceRoutes,
  ...complianceRouteDescriptors,
  ...integrationsRoutes,
  ...administrationRoutes,
  ...profileRoutes,
]

const ZERO_GUID = '00000000-0000-0000-0000-000000000000'

// The paywall names the routes it gates by string key. Renaming a route above
// would drop it from the gate silently, which fails open, so the link is
// checked here where a mismatch is loud rather than invisible.
if (import.meta.env.DEV) {
  const missing = assertAdvicePrepPaywalledRoutesExist(
    new Set(protectedRouteDescriptors.map((descriptor) => descriptor.key))
  )
  if (missing.length) {
    console.error(
      `AdvicePrep paywall references routes that do not exist: ${missing.join(', ')}. ` +
        'Those areas are no longer gated.'
    )
  }
}

export function renderProtectedRoutes() {
  return protectedRouteDescriptors.map(renderProtectedRoute)
}

/**
 * `forceVisibleRouteKeys` keeps an item in the nav even when its permission is
 * missing. Used by the AdvicePrep paywall: the server withholds those
 * permissions, and hiding the items would leave the user no way to reach the
 * screen explaining how to get them back.
 */
export function getProtectedNavSections(
  session: AuthSession | null,
  userPermissions: Set<PermissionKey>,
  forceVisibleRouteKeys?: Set<string>
) {
  if (!session) return []

  const navSections = new Map<
    string,
    {
      key: string
      label: string
      order: number
      items: Array<{ key: string; label: string; path: string; Icon: NonNullable<ProtectedRouteDescriptor['nav']>['Icon']; order: number }>
    }
  >()

  protectedRouteDescriptors.forEach((descriptor, descriptorIndex) => {
    if (!descriptor.nav) return
    const forced = forceVisibleRouteKeys?.has(descriptor.key) ?? false
    if (!forced && !hasRouteAccess(userPermissions, descriptor.access)) return
    if (!forced && !hasRouteAccess(userPermissions, descriptor.nav.access)) return
    if (!hasRouteVisibility(session, descriptor.visibility)) return

    const sectionConfig = getNavSectionConfig(descriptor.nav.sectionKey)
    if (!sectionConfig) return

    const resolvedPath = resolveRoutePath(`/${descriptor.path}`, session)
    if (!resolvedPath) return

    const section = navSections.get(descriptor.nav.sectionKey) ?? {
      key: descriptor.nav.sectionKey,
      label: sectionConfig.label,
      order: sectionConfig.order,
      items: [],
    }

    section.items.push({
      key: descriptor.key,
      label: descriptor.nav.label,
      path: resolvedPath,
      Icon: descriptor.nav.Icon,
      order: descriptor.nav.order ?? descriptorIndex,
    })

    navSections.set(descriptor.nav.sectionKey, section)
  })

  return Array.from(navSections.values())
    .sort((a, b) => a.order - b.order)
    .map<ResolvedNavSection>((section) => ({
      key: section.key,
      label: section.label,
      items: section.items
        .sort((a, b) => a.order - b.order)
        .map(({ order: _order, ...item }) => item),
    }))
}

export function resolveFirstAccessibleProtectedPath(session: AuthSession | null, userPermissions: Set<PermissionKey>) {
  const sections = getProtectedNavSections(session, userPermissions)
  return sections[0]?.items[0]?.path ?? null
}

function getEffectiveUserType(session: AuthSession) {
  if (session.currentUserId === ZERO_GUID) return UserTypes.UnregisteredUser
  return session.currentUserTypeId
}

function getPreferredProtectedPaths(session: AuthSession): string[] {
  const clientDashboardPath = session.currentClientId ? '/report' : null

  switch (getEffectiveUserType(session)) {
    case UserTypes.Owner:
    case UserTypes.Admin:
      return ['/clients', '/admin', '/reports/created', '/profile']
    case UserTypes.ClientAdmin:
      if (session.currentClientTypeId === ClientTypes.Advisor) {
        return ['/clients', '/reports/created', '/billing', '/users', '/profile']
      }
      return [clientDashboardPath, '/reports/created', '/billing', '/users', '/platform', '/profile'].filter(
        (path): path is string => Boolean(path)
      )
    case UserTypes.ClientUser:
      if (session.currentClientTypeId === ClientTypes.Advisor) {
        return ['/clients', '/reports/created', '/users', '/profile']
      }
      return [clientDashboardPath, '/users', '/reports/created', '/platform', '/profile'].filter(
        (path): path is string => Boolean(path)
      )
    case UserTypes.User:
      return ['/users', '/reports/created', '/profile']
    case UserTypes.UnregisteredUser:
      return ['/profile']
    default:
      return [clientDashboardPath, '/clients', '/users', '/reports/created', '/profile'].filter(
        (path): path is string => Boolean(path)
      )
  }
}

export function hasProtectedPathAccess(targetPath: string, session: AuthSession, userPermissions: Set<PermissionKey>) {
  return protectedRouteDescriptors.some((descriptor) => {
    if (!hasRouteAccess(userPermissions, descriptor.access)) return false
    if (!hasRouteVisibility(session, descriptor.visibility)) return false

    const resolvedPath = resolveRoutePath(`/${descriptor.path}`, session)
    if (!resolvedPath) return false

    return Boolean(matchPath({ path: resolvedPath, end: true }, targetPath))
  })
}

export function resolvePreferredProtectedPath(session: AuthSession | null, userPermissions: Set<PermissionKey>) {
  if (!session) return null

  for (const candidate of getPreferredProtectedPaths(session)) {
    if (hasProtectedPathAccess(candidate, session, userPermissions)) {
      return candidate
    }
  }

  return resolveFirstAccessibleProtectedPath(session, userPermissions)
}
